import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/lib/auth";

import Landing from "@/pages/Landing";
import Pricing from "@/pages/Pricing";
import About from "@/pages/About";
import FAQ from "@/pages/FAQ";
import LegalStub from "@/pages/LegalStub";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import LockAccount from "@/pages/LockAccount";
import AuthCallback from "@/pages/AuthCallback";

import SchoolDashboard from "@/pages/school/SchoolDashboard";
import SchoolStudents from "@/pages/school/SchoolStudents";
import SchoolFees from "@/pages/school/SchoolFees";
import SchoolTransactions from "@/pages/school/SchoolTransactions";

import ParentDashboard from "@/pages/parent/ParentDashboard";
import ParentPaySchool from "@/pages/parent/ParentPaySchool";
import ParentLoans from "@/pages/parent/ParentLoans";
import ParentAcademicAdvance from "@/pages/parent/ParentAcademicAdvance";
import ParentALAVerifyEmail from "@/pages/parent/ParentALAVerifyEmail";
import PaymentSuccess from "@/pages/parent/PaymentSuccess";
import PaymentCallback from "@/pages/parent/PaymentCallback";

import AgentDashboard from "@/pages/agent/AgentDashboard";

import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminLoans from "@/pages/admin/AdminLoans";
import AdminALAPayouts from "@/pages/admin/AdminALAPayouts";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminFeeConfig from "@/pages/admin/AdminFeeConfig";
import AdminTransactions from "@/pages/admin/AdminTransactions";
import AdminStressTest from "@/pages/admin/AdminStressTest";
import AdminKYC from "@/pages/admin/AdminKYC";

import CreditApproverDashboard from "@/pages/approver/CreditApproverDashboard";
import CreditApproverLoans from "@/pages/approver/CreditApproverLoans";
import ReviewerQueue from "@/pages/approver/ReviewerQueue";
import ReviewDetail from "@/pages/reviews/ReviewDetail";
import AdminReviewers from "@/pages/admin/AdminReviewers";

function Protected({ roles, children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

function RoleHome() {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  const map = { admin: "/admin", school: "/school", parent: "/parent", agent: "/agent", credit_reviewer: "/approver" };
  return <Navigate to={map[user.role] || "/"} replace />;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster richColors position="top-right" />
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/about" element={<About />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/terms" element={<LegalStub title="Terms of Service" description="Terms of Service for using the Ascentremit website and app." testid="legal-terms" />} />
          <Route path="/privacy" element={<LegalStub title="Privacy Policy" description="How Ascentremit collects, uses and protects your personal data under the Nigeria Data Protection Act 2023." testid="legal-privacy" />} />
          <Route path="/cookies" element={<LegalStub title="Cookie Policy" description="How Ascentremit uses cookies on ascentremit.com." testid="legal-cookies" />} />
          <Route path="/loan-terms" element={<LegalStub title="Loan Terms & Conditions" description="The terms that apply to school-fee instalment plans provided by Ascentremit." testid="legal-loan-terms" />} />
          <Route path="/responsible-lending" element={<LegalStub title="Responsible Lending & Collections" description="How Ascentremit lends responsibly and treats customers fairly if they fall behind." testid="legal-responsible-lending" />} />
          <Route path="/complaints" element={<LegalStub title="Make a complaint" description="How to raise a complaint with Ascentremit and what happens next." testid="legal-complaints" />} />
          <Route path="/struggling-to-repay" element={<LegalStub title="Struggling to repay?" description="Options available if you're having difficulty keeping up with your Ascentremit instalment plan." testid="legal-struggling" />} />
          <Route path="/careers" element={<LegalStub title="Careers" description="Open roles at Ascentremit." testid="legal-careers" />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/lock-account" element={<LockAccount />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/dashboard" element={<RoleHome />} />

          {/* School */}
          <Route path="/school" element={<Protected roles={["school"]}><SchoolDashboard /></Protected>} />
          <Route path="/school/students" element={<Protected roles={["school"]}><SchoolStudents /></Protected>} />
          <Route path="/school/fees" element={<Protected roles={["school"]}><SchoolFees /></Protected>} />
          <Route path="/school/transactions" element={<Protected roles={["school"]}><SchoolTransactions /></Protected>} />

          {/* Parent */}
          <Route path="/parent" element={<Protected roles={["parent"]}><ParentDashboard /></Protected>} />
          <Route path="/parent/pay" element={<Protected roles={["parent"]}><ParentPaySchool /></Protected>} />
          <Route path="/parent/wallet" element={<Navigate to="/parent/pay" replace />} />
          <Route path="/parent/loans" element={<Protected roles={["parent"]}><ParentLoans /></Protected>} />
          <Route path="/parent/academic-advance" element={<Protected roles={["parent"]}><ParentAcademicAdvance /></Protected>} />
          <Route path="/parent/ala/verify-email" element={<ParentALAVerifyEmail />} />
          <Route path="/payment/success" element={<Protected><PaymentSuccess /></Protected>} />
          <Route path="/payment/callback" element={<Protected><PaymentCallback /></Protected>} />
          <Route path="/payment/cancel" element={<Protected roles={["parent"]}><ParentDashboard /></Protected>} />

          {/* Agent */}
          <Route path="/agent" element={<Protected roles={["agent"]}><AgentDashboard /></Protected>} />

          {/* Admin */}
          <Route path="/admin" element={<Protected roles={["admin"]}><AdminDashboard /></Protected>} />
          <Route path="/admin/loans" element={<Protected roles={["admin"]}><AdminLoans /></Protected>} />
          <Route path="/admin/ala-payouts" element={<Protected roles={["admin"]}><AdminALAPayouts /></Protected>} />
          <Route path="/admin/users" element={<Protected roles={["admin"]}><AdminUsers /></Protected>} />
          <Route path="/admin/fee-config" element={<Protected roles={["admin"]}><AdminFeeConfig /></Protected>} />
          <Route path="/admin/transactions" element={<Protected roles={["admin"]}><AdminTransactions /></Protected>} />
          <Route path="/admin/stress-test" element={<Protected roles={["admin"]}><AdminStressTest /></Protected>} />
          <Route path="/admin/kyc" element={<Protected roles={["admin"]}><AdminKYC /></Protected>} />
          <Route path="/admin/reviewers" element={<Protected roles={["admin"]}><AdminReviewers /></Protected>} />
          <Route path="/admin/review/:kind/:id" element={<Protected roles={["admin"]}><ReviewDetail /></Protected>} />

          {/* Credit Reviewer */}
          <Route path="/approver" element={<Protected roles={["credit_reviewer"]}><CreditApproverDashboard /></Protected>} />
          <Route path="/approver/loans" element={<Protected roles={["credit_reviewer"]}><CreditApproverLoans /></Protected>} />
          <Route path="/approver/queue" element={<Protected roles={["credit_reviewer"]}><ReviewerQueue /></Protected>} />
          <Route path="/approver/review/:kind/:id" element={<Protected roles={["credit_reviewer"]}><ReviewDetail /></Protected>} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
